import React, { useEffect, useRef, useState } from "react"
import PropTypes from "prop-types"
import { navigate } from "gatsby"
import styled from "styled-components"
import { css } from "styled-components"

import tokens from "../_tokens"
import { ButtonInvisible, Icon, Link } from "./"
import { Draggable } from "react-beautiful-dnd"

function EditableInput(props) {
  // We use hooks to declare "initial" states
  const inputRef = useRef(null)
  const [inputVisible, setInputVisible] = useState(false)
  const [text, setText] = useState(props.text)

  function onClickOutSide(e) {
    // Check if user is clicking outside of <input>
    if (inputRef.current && !inputRef.current.contains(e.target)) {
      setInputVisible(false) // Disable text input
    }
  }

  useEffect(() => {
    // Handle outside clicks on mounted state
    if (inputVisible) {
      document.addEventListener("mousedown", onClickOutSide)
    }

    // This is a necessary step to "dismount" unnecessary events when we destroy the component
    return () => {
      document.removeEventListener("mousedown", onClickOutSide)
    }
  })

  let timer = 0
  let delay = 200
  let prevent = false

  function doClickAction() {
    navigate(props.url)
  }
  function doDoubleClickAction() {
    setInputVisible(true)
  }

  function handleClick() {
    timer = setTimeout(function () {
      if (!prevent) {
        doClickAction()
      }
      prevent = false
    }, delay)
  }

  function handleDoubleClick() {
    clearTimeout(timer)
    prevent = true
    doDoubleClickAction()
  }

  function changeMenuItem(e) {
    setText(e.target.value)
    props.changeMenuItem(e)
  }

  return (
    <>
      {inputVisible ? (
        <input
          id={props.id}
          ref={inputRef} // Set the Ref
          value={text} // Now input value uses local state
          onChange={changeMenuItem}
        />
      ) : (
        <span
          css={{ padding: `${tokens.size.base}px ${1.5 * tokens.size.base}px` }}
          onClick={handleClick}
          onDoubleClick={handleDoubleClick}
        >
          {text}
        </span>
      )}
    </>
  )
}

EditableInput.propTypes = {
  id: PropTypes.string,
  text: PropTypes.string,
  url: PropTypes.string,
  changeMenuItem: PropTypes.func,
}


const ButtonRemove = props => (
  <ButtonInvisible {...props}>
    <Icon name="trash" width="20" height="20" />
  </ButtonInvisible>
)

const ButtonAdd = props => (
  <ButtonInvisible {...props}>
    <Icon name="plus" width="20" height="20" />
  </ButtonInvisible>
)

const StyledButtonGroup = styled.span`
  color: ${tokens.color.primary};
  display: flex;
  opacity: 0;
  margin-left: auto;
  align-items: center;
`

const DragHandle = styled.span`
  position: absolute;
  left: 0;
  transition: all 0.15s ease-in-out 0s;
  cursor: pointer;
  opacity: 0;
  transform: translate(-24px, -50%);

  display: flex;
  top: 50%;
  transform: translateY(-50%);
  width: 20px;
  height: 18px;
  padding: 10px 0;
  overflow: hidden;
  line-height: 5px;
  vertical-align: middle;
  font-size: 11px;
  font-family: sans-serif;
  letter-spacing: 1px;
  color: ${tokens.color.text.light};
  text-shadow: 1px 0 1px ${tokens.color.text.muted};

  &::after {
    content: ".. .. ..";
  }
`

const StyledDraggableItem = styled.span`
  user-select: none;
  cursor: pointer;
  background: ${tokens.color.white};
  padding: 10px;
  border: ${props =>
    props.isDragging ? "1px solid " + tokens.color.primary : "0"};
  display: inline-flex;
  width: 100%;
  display: flex;
  align-items: center;
`

const NavItemEditable = ({
  item,
  index,
  isParent,
  dragHandleProps,
  deleteItem,
}) => {
  const Buttons = () =>
    isParent ? (
      <StyledButtonGroup>
        <ButtonAdd id={item.url} />
        <ButtonRemove
          css={{ marginLeft: "5px" }}
          id={item.url}
          isParentItem={isParent}
          onClick={deleteItem}
        />
      </StyledButtonGroup>
    ) : (
      <StyledButtonGroup>
        <ButtonRemove
          isParentItem={isParent}
          id={item.url}
          onClick={deleteItem}
        />
      </StyledButtonGroup>
    )

  const Item = ({ dragHandleProps, ...props }) => (
    <>
      <DragHandle {...dragHandleProps} />
      {/*  TODO: Removed until further discussion
      <EditableInput
        id={item.id}
        url={item.url}
        text={item.title}
        changeMenuItem={changeMenuItem.bind(this)}
        {...props}
      // */}
      <NavItemPlain item={item} {...props} />
      <Buttons />
    </>
  )

  Item.propTypes = {
    dragHandleProps: PropTypes.object,
  }

  return isParent ? (
    <Item dragHandleProps={dragHandleProps} />
  ) : (
    <Draggable draggableId={item.url} index={index} key={item.url}>
      {(provided, snapshot) => (
        <>
          <StyledDraggableItem
            style={{ display: "flex" }}
            ref={provided.innerRef}
            isDragging={snapshot.isDragging}
            {...provided.draggableProps}
          >
            <Item dragHandleProps={provided.dragHandleProps} />
          </StyledDraggableItem>
          {provided.placeholder}
        </>
      )}
    </Draggable>
  )
}

NavItemEditable.propTypes = {
  deleteItem: PropTypes.func,
  dragHandleProps: PropTypes.object,
  index: PropTypes.number,
  isParent: PropTypes.bool,
  item: PropTypes.shape({
    id: PropTypes.string,
    title: PropTypes.string,
    url: PropTypes.string,
  }),
}

const StyledNavLink = styled(Link)`
  font-size: inherit;
  color: inherit;
  &.active {
    font-weight: 700;
  }
`

const NavItemPlain = ({ item, ...props }) => (
  <StyledNavLink
    to={item.url}
    activeClassName="active"
    partiallyActive={true}
    {...props}
  >
    {item.title}
  </StyledNavLink>
)

NavItemPlain.propTypes = {
  item: PropTypes.shape({
    title: PropTypes.string,
    url: PropTypes.string,
  }),
}

const StyledNavItem = styled.li`
  list-style: none;
  position: relative;
  flex-wrap: nowrap;
  display: flex;
  padding: ${({ isParent }) =>
    isParent
      ? `${tokens.size.base}px`
      : `${0.5 * tokens.size.base}px ${tokens.size.base}px`};
  font-size: ${({ isParent }) =>
    isParent ? tokens.fontSize.heading.m : tokens.fontSize.heading.s};
  color: ${({ isParent }) =>
    isParent ? tokens.color.base : tokens.color.primary};
  // Don't use margin on child items, it breaks react-beautiful-dnd styling
  margin-bottom: ${({ isParent }) =>
    isParent ? `${2 * tokens.size.base}px` : `0`};

  &:hover ${DragHandle} {
    opacity: 1;
    transform: translate(-10px, -50%);
  }

  &:hover ${StyledButtonGroup} {
    opacity: 1;
  }
`

const NavItem = ({
  item,
  children,
  index,
  isParent,
  changeMenuItem,
  deleteItem,
  ...props
}) => {
  return (
    <StyledNavItem
      isParent={isParent}
      style={isParent ? {} : { padding: 0 }} // remove padding on item - it will be applied on draggable container. Otherwise the draggable menu styling will be broken
    >
      {__EDIT__ ? (
        <NavItemEditable
          item={item}
          index={index}
          isParent={isParent}
          changeMenuItem={changeMenuItem}
          deleteItem={deleteItem}
          {...props}
        >
          {children}
        </NavItemEditable>
      ) : (
        <NavItemPlain item={item} {...props}>
          {children}
        </NavItemPlain>
      )}
    </StyledNavItem>
  )
}

NavItem.propTypes = {
  deleteItem: PropTypes.func,
  changeMenuItem: PropTypes.func,
  children: PropTypes.node,
  index: PropTypes.number,
  isParent: PropTypes.bool,
  item: PropTypes.object,
}

export default NavItem
